import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, Users, Send, TrendingUp, Loader2, Monitor, Globe,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALETTE = ["#059669", "#0E3B3B", "#34d399", "#94a3b8", "#f59e0b", "#0ea5e9", "#a855f7"];

function fmtDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-2 text-slate-500"><Icon size={16} className="text-emerald-600" /><span className="font-mono-tech text-[11px] uppercase tracking-wider">{label}</span></div>
    <div className="mt-2 font-display font-extrabold text-3xl text-[#0B0F14]">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

export default function AnalyticsPanel({ token, onExpire }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [a, r] = await Promise.all([
        axios.get(`${API}/admin/analytics?days=${days}`, { headers }),
        axios.get(`${API}/admin/analytics/recent`, { headers }),
      ]);
      setData(a.data);
      setRecent(r.data);
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load analytics.");
    } finally { setLoading(false); }
  }, [token, days, onExpire]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="grid place-items-center py-20 text-slate-500"><Loader2 size={28} className="animate-spin" /></div>;
  if (!data) return null;

  const maxRef = Math.max(...data.referrers.map((r) => r.count), 1);

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Range selector */}
      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`range-${d}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${days === d ? "bg-[#0E3B3B] text-white" : "bg-white border border-[#E7E5DF] text-[#0B0F14] hover:border-emerald-600/50"}`}>
            Last {d} days
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Page Views" value={data.total_views.toLocaleString()} />
        <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors.toLocaleString()} />
        <StatCard icon={Send} label="Conversions" value={data.conversions.toLocaleString()} sub="Quote/contact submissions" />
        <StatCard icon={TrendingUp} label="Conversion Rate" value={`${data.conversion_rate}%`} sub="Leads per unique visitor" />
      </div>

      {/* Traffic over time */}
      <div className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid="chart-traffic">
        <h3 className="font-display font-bold text-[#0B0F14] mb-4">Traffic Over Time</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.daily} margin={{ left: -18, right: 8, top: 4 }}>
            <defs>
              <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#059669" stopOpacity={0.35} /><stop offset="100%" stopColor="#059669" stopOpacity={0} /></linearGradient>
              <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0E3B3B" stopOpacity={0.3} /><stop offset="100%" stopColor="#0E3B3B" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <Tooltip labelFormatter={fmtDay} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 13 }} />
            <Area type="monotone" dataKey="views" name="Views" stroke="#059669" strokeWidth={2} fill="url(#gv)" />
            <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#0E3B3B" strokeWidth={2} fill="url(#gu)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top pages */}
        <div className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid="chart-top-pages">
          <h3 className="font-display font-bold text-[#0B0F14] mb-4">Top Pages</h3>
          {data.top_pages.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={Math.max(180, data.top_pages.length * 34)}>
              <BarChart data={data.top_pages} layout="vertical" margin={{ left: 8, right: 16 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11, fill: "#475569" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 13 }} />
                <Bar dataKey="count" name="Views" radius={[0, 6, 6, 0]} fill="#059669" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Devices */}
        <div className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid="chart-devices">
          <h3 className="font-display font-bold text-[#0B0F14] mb-4 flex items-center gap-2"><Monitor size={16} className="text-emerald-600" /> Devices & Browsers</h3>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400 mb-2">Device</p>
              {data.devices.map((d, i) => (
                <div key={d.name} className="flex items-center justify-between text-sm py-1">
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{d.name}</span>
                  <span className="text-slate-500">{d.count}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400 mb-2">Browser</p>
              {data.browsers.map((d, i) => (
                <div key={d.name} className="flex items-center justify-between text-sm py-1">
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETTE[(i + 2) % PALETTE.length] }} />{d.name}</span>
                  <span className="text-slate-500">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Referrers */}
        <div className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid="chart-referrers">
          <h3 className="font-display font-bold text-[#0B0F14] mb-4 flex items-center gap-2"><Globe size={16} className="text-emerald-600" /> Traffic Sources</h3>
          {data.referrers.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">No data yet.</p> : (
            <div className="space-y-3">
              {data.referrers.map((r, i) => (
                <div key={r.name}>
                  <div className="flex items-center justify-between text-sm mb-1"><span className="text-[#0B0F14]">{r.name}</span><span className="text-slate-500">{r.count}</span></div>
                  <div className="h-2 rounded-full bg-[#F0EFEA] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.count / maxRef) * 100}%`, background: PALETTE[i % PALETTE.length] }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent visitors */}
        <div className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid="recent-visitors">
          <h3 className="font-display font-bold text-[#0B0F14] mb-4">Recent Visitors</h3>
          {recent.length === 0 ? <p className="text-slate-400 text-sm py-8 text-center">No visitors tracked yet.</p> : (
            <div className="divide-y divide-[#F0EFEA] max-h-[320px] overflow-y-auto">
              {recent.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="text-[#0B0F14] truncate font-medium">{v.path}</p>
                    <p className="text-slate-400 text-xs">{v.device} · {v.browser} · {v.ref_source}</p>
                  </div>
                  <span className="text-slate-400 text-xs whitespace-nowrap">{fmtTime(v.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
